import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Typography, 
  Paper, 
  Box, 
  Alert,
  Divider,
  Grid,
  TextField,
  Button
} from '@mui/material';
import { useAuth } from '../context/AuthContext';
import DynamicForm from '../components/DynamicForm';
import axios from '../utils/axios';

const Profile = () => {
  const { user } = useAuth();
  const [profileData, setProfileData] = useState(null);
  const [dynamicFields, setDynamicFields] = useState([]);
  const [dynamicValues, setDynamicValues] = useState({});
  const [basicData, setBasicData] = useState({
    name: '',
    email: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    fetchProfileData();
  }, []);

  const fetchProfileData = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/users/profile/dynamic');
      const data = response.data;
      
      setProfileData(data);
      setDynamicFields(data.fieldConfiguration || []);
      setDynamicValues(data.dynamicFields || {});
      setBasicData({
        name: data.name || '',
        email: data.email || '',
      });
    } catch (error) {
      setError('Failed to load profile data');
    } finally {
      setLoading(false);
    }
  };

  const handleBasicDataChange = (e) => {
    const { name, value } = e.target;
    setBasicData(prev => ({ ...prev, [name]: value }));
  };

  const handleDynamicDataChange = (newData) => {
    setDynamicValues(newData);
  };

  const handleBasicSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError('');
      setSuccess('');
      
      await axios.put('/users/profile', basicData);
      setSuccess('Basic information updated successfully!');
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to update basic information');
    } finally {
      setLoading(false);
    }
  };

  const handleDynamicSubmit = async (formData) => {
    try {
      setLoading(true);
      setError('');
      setSuccess('');
      
      await axios.put('/users/profile/dynamic', {
        dynamicFields: formData
      });
      setSuccess('Profile updated successfully!');
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  if (loading && !profileData) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          Profile
        </Typography>
        <Paper sx={{ p: 3 }}>
          <Typography>Loading...</Typography>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" gutterBottom>
        Profile
      </Typography>
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Basic Information */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Basic Information
            </Typography>
            <Box component="form" onSubmit={handleBasicSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                label="Name"
                name="name"
                value={basicData.name}
                onChange={handleBasicDataChange}
                required
                fullWidth
              />
              <TextField
                label="Email"
                name="email"
                type="email"
                value={basicData.email}
                onChange={handleBasicDataChange}
                fullWidth
              />
              <TextField
                label="Mobile Number"
                value={user?.mobileNumber || 'Not set'}
                disabled
                fullWidth
                helperText="Mobile number cannot be changed"
              />
              <Button
                type="submit"
                variant="contained"
                disabled={loading}
                sx={{ alignSelf: 'flex-start' }}
              >
                {loading ? 'Updating...' : 'Update Basic Info'}
              </Button>
            </Box>
          </Paper>
        </Grid>

        {/* Dynamic Fields */}
        {dynamicFields.length > 0 && (
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Additional Information
              </Typography>
              <Divider sx={{ mb: 3 }} />
              <DynamicForm
                fields={dynamicFields}
                values={dynamicValues}
                onChange={handleDynamicDataChange}
                onSubmit={handleDynamicSubmit}
                loading={loading}
                error=""
              />
            </Paper>
          </Grid>
        )}

        {/* Profile Summary */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Profile Summary
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Typography variant="body2">
                <strong>Role:</strong> {user?.role || 'User'}
              </Typography>
              <Typography variant="body2">
                <strong>Account Status:</strong> {user?.isVerified ? 'Verified' : 'Not Verified'}
              </Typography>
              <Typography variant="body2">
                <strong>Member Since:</strong> {profileData?.createdAt ? new Date(profileData.createdAt).toLocaleDateString() : 'N/A'}
              </Typography>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default Profile; 